import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

const HOOKS_ES = (name) => [
  `¿No sabes qué hacer? Pregúntale a ${name} 💕`,
  `¿Quieres las mejores ofertas? Pregúntale a ${name} ✨`,
  `¿Primera vez en la Riviera? ${name} te guía 🌴`,
];
const HOOKS_EN = (name) => [
  `Don't know what to do? Ask ${name} 💕`,
  `Want the best deals? Ask ${name} ✨`,
  `First time in the Riviera? ${name} will guide you 🌴`,
];

function getSessionId() {
  let id = localStorage.getItem("benova_val_session");
  if (!id) {
    id = `val_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("benova_val_session", id);
  }
  return id;
}

export default function ValChat() {
  const { settings } = useSettings();
  const { t, lang } = useLang();
  const valName = settings.val_name || "Val";
  const HOOKS = lang === "en" ? HOOKS_EN(valName) : HOOKS_ES(valName);
  const [open, setOpen] = useState(false);
  const [hook, setHook] = useState(HOOKS[0]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(getSessionId());

  useEffect(() => {
    setHook(HOOKS[0]);
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % HOOKS.length;
      setHook(HOOKS[i]);
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [lang, valName]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: settings.val_welcome_message || `¡Hola! Soy ${valName} 💕` }]);
    }
  }, [open, settings.val_welcome_message]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await api.post("/val/chat", {
        session_id: sessionIdRef.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: t("val.error") }]);
    } finally {
      setLoading(false);
    }
  };

  const avatar = settings.val_avatar || "https://images.unsplash.com/photo-1587403655231-b1734312903f?q=80&w=400&auto=format&fit=crop";

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
        <AnimatePresence>
          {!open && (
            <motion.button
              key="hook"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onClick={() => setOpen(true)}
              className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg"
              style={{
                background: "var(--bn-surface)",
                color: "var(--bn-text)",
                border: "1px solid var(--bn-border)",
                fontFamily: "var(--bn-font-body)",
                fontSize: ".85rem",
              }}
              data-testid="val-hook-btn"
            >
              <Sparkles size={14} style={{ color: "var(--bn-accent)" }} />
              {hook}
            </motion.button>
          )}
        </AnimatePresence>

        <button
          onClick={() => setOpen(!open)}
          className="bn-pulse"
          style={{
            width: 64,
            height: 64,
            borderRadius: "9999px",
            background: "var(--bn-accent)",
            color: "var(--bn-accent-fg)",
            border: "3px solid var(--bn-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 10px 30px -5px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}
          data-testid="val-chat-toggle"
          aria-label={`Chat con ${valName}`}
        >
          {open ? (
            <X size={22} />
          ) : (
            <img src={avatar} alt={valName} className="w-full h-full object-cover" />
          )}
        </button>
      </div>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-40 w-[calc(100vw-2.5rem)] max-w-sm bn-card"
            style={{
              maxHeight: "min(70vh, 600px)",
              display: "flex",
              flexDirection: "column",
              background: "var(--bn-surface)",
              border: "1px solid var(--bn-border)",
              borderRadius: "var(--bn-card-radius)",
              overflow: "hidden",
              boxShadow: "0 30px 80px -20px rgba(0,0,0,0.4)",
            }}
            data-testid="val-chat-window"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4" style={{ borderBottom: "1px solid var(--bn-border)", background: "var(--bn-bg)" }}>
              <img src={avatar} alt={valName} className="w-11 h-11 rounded-full object-cover" style={{ border: "2px solid var(--bn-accent)" }} />
              <div>
                <div className="bn-heading text-lg leading-none">{valName}</div>
                <div className="bn-eyebrow mt-1 text-[.65rem]" style={{ color: "var(--bn-accent)" }}>• {t("val.online")}</div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "var(--bn-bg)" }}>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[80%] px-4 py-2.5 text-[.9rem] leading-relaxed"
                    style={{
                      background: m.role === "user" ? "var(--bn-primary)" : "var(--bn-surface)",
                      color: m.role === "user" ? "var(--bn-primary-fg)" : "var(--bn-text)",
                      border: m.role === "user" ? "none" : "1px solid var(--bn-border)",
                      borderRadius: "14px",
                      whiteSpace: "pre-wrap",
                    }}
                    data-testid={`val-msg-${m.role}`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-4 py-2.5 flex gap-1" style={{ background: "var(--bn-surface)", borderRadius: 14, border: "1px solid var(--bn-border)" }}>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--bn-text-muted)", animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--bn-text-muted)", animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--bn-text-muted)", animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 flex gap-2" style={{ borderTop: "1px solid var(--bn-border)", background: "var(--bn-surface)" }}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder={`Pregúntale a ${valName}...`}
                className="bn-input flex-1"
                style={{ padding: ".7rem 1rem", fontSize: ".9rem" }}
                data-testid="val-input"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                className="bn-btn-primary"
                style={{ padding: ".65rem .9rem", opacity: (loading || !input.trim()) ? 0.5 : 1 }}
                data-testid="val-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
